<template>
  <div class="aprod">
    <section class="aprod__filters" data-tour="admin-products-status">
      <button
        v-for="tab in statusTabs"
        :key="tab.key"
        type="button"
        class="sui-card aprod__filter"
        :class="{ 'is-active': status === tab.key, 'is-alert': tab.alert }"
        @click="setStatus(tab.key)"
      >
        <span class="aprod__filter-label">{{ tab.label }}</span>
        <span class="aprod__filter-count">{{ tab.count }}</span>
      </button>
    </section>

    <section class="sui-card sui-card--flush">
      <div class="sui-toolbar" data-tour="admin-products-toolbar">
        <div class="aprod__exposure" role="group" aria-label="몰 노출 필터" data-tour="admin-products-exposure">
          <button
            v-for="chip in exposureChips"
            :key="chip.key"
            type="button"
            class="aprod__chip"
            :class="{ 'is-active': exposure === chip.key }"
            @click="setExposure(chip.key)"
          >
            {{ chip.label }}
            <em>{{ chip.count }}</em>
          </button>
          <button
            type="button"
            class="aprod__chip"
            :class="{ 'is-active': stockLow }"
            data-tour="admin-products-stock-low"
            @click="toggleStockLow"
          >
            저재고
            <em>{{ lowStockCount }}</em>
          </button>
        </div>
        <div class="sui-presets" role="group" aria-label="최근 변경 기간">
          <button
            v-for="item in presets"
            :key="item.key"
            type="button"
            :class="{ 'is-active': preset === item.key }"
            @click="applyPreset(item.key)"
          >
            {{ item.label }}
          </button>
        </div>
        <label class="sui-date">
          <span>시작일</span>
          <input v-model="fromDate" type="date" @change="onManualDateChange" />
        </label>
        <label class="sui-date">
          <span>종료일</span>
          <input v-model="toDate" type="date" @change="onManualDateChange" />
        </label>
        <label class="sui-search">
          <AppIcon icon="mdi-magnify" :size="17" />
          <input
            v-model.trim="keyword"
            type="search"
            placeholder="상품명 · 상품코드 · 판매업체 검색"
            @keydown.enter="reloadAndSync"
          />
        </label>
        <button type="button" class="sui-btn" @click="reloadAndSync">검색</button>
        <select v-model="sortKey" class="sui-select" aria-label="정렬" data-tour="admin-products-sort">
          <option value="newest">최근 변경순</option>
          <option value="marginDesc">관리자 마진 높은순</option>
          <option value="marginAsc">관리자 마진 낮은순</option>
          <option value="priceDesc">판매가 높은순</option>
          <option value="soldDesc">판매량 높은순</option>
        </select>
        <span class="sui-toolbar__spacer" />
        <button type="button" class="sui-btn" :disabled="loading || busyCode !== ''" @click="reload">
          <AppIcon icon="mdi-refresh" :size="16" />
          새로고침
        </button>
      </div>

      <p v-if="errorMessage" class="aprod__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ errorMessage }}
      </p>
      <p v-else-if="actionError" class="aprod__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ actionError }}
      </p>

      <div v-if="visibleProducts.length" class="sui-table-wrap" data-tour="admin-products-table">
        <table class="sui-table">
          <thead>
            <tr>
              <th>상품</th>
              <th>판매 · 노출</th>
              <th class="is-num">가격 · 할인</th>
              <th class="is-num">관리자 마진</th>
              <th class="is-num">판매 성과</th>
              <th class="is-center">재고</th>
              <th>판매업체</th>
              <th class="is-center">관리</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in visibleProducts" :key="product.code">
              <td>
                <span class="sui-prod">
                  <img
                    v-if="product.thumbnail"
                    class="sui-prod__img"
                    :src="product.thumbnail"
                    :alt="product.name"
                    loading="lazy"
                  />
                  <span v-else class="aprod__noimg">
                    <AppIcon icon="mdi-image-off-outline" :size="16" />
                  </span>
                  <span>
                    <button
                      type="button"
                      class="sui-prod__name-btn"
                      data-tour="admin-products-preview"
                      :title="`${product.name} 몰 미리보기`"
                      @click="openPreview(product)"
                    >
                      {{ product.name }}
                    </button>
                    <span class="sui-table__sub">{{ product.code }} · {{ product.category }}</span>
                  </span>
                </span>
              </td>
              <td>
                <span class="aprod__chips">
                  <span class="sui-badge" :class="statusTone(product.status)">
                    {{ statusLabel(product.status) }}
                  </span>
                  <span
                    class="aprod__tag"
                    :class="isLive(product) ? 'aprod__tag--live' : 'aprod__tag--offline'"
                    data-tour="admin-products-mall-live"
                  >
                    {{ mallLiveLabel(product.status) }}
                  </span>
                  <span v-if="product.isDeal" class="aprod__tag aprod__tag--deal">이달의 특가</span>
                  <span v-if="product.badge" class="aprod__tag" :class="badgeTone(product.badge)">
                    {{ product.badge }}
                  </span>
                </span>
                <span v-if="product.status === 'REJECTED' && product.rejectReason" class="aprod__reason">
                  {{ product.rejectReason }}
                </span>
                <span v-else-if="product.status === 'REQUESTED'" class="sui-table__sub">
                  요청 {{ formatDate(product.requestedAt) }}
                  <template v-if="(product.requestCount || 0) > 1">
                    · 검수요청 {{ product.requestCount }}회
                  </template>
                </span>
                <span v-else class="sui-table__sub">
                  변경 {{ formatDate(product.updatedAt || product.reviewedAt || product.createdAt) }}
                  <template v-if="(product.requestCount || 0) > 0">
                    · 검수요청 {{ product.requestCount }}회
                  </template>
                </span>
              </td>
              <td class="is-num">
                <span class="sui-table__strong">{{ formatWon(product.price) }}원</span>
              </td>
              <td class="is-num">
                <span
                  class="sui-table__strong"
                  :class="{ 'is-loss': adminMarginOf(product).negative }"
                >
                  {{ formatWon(adminMarginOf(product).platform) }}원
                </span>
                <span class="sui-table__sub" :class="{ 'is-loss': adminMarginOf(product).negative }">
                  마진율 {{ adminMarginRate(product) }}%
                  · 배분 {{ adminMarginOf(product).share }}%
                </span>
              </td>
              <td class="is-num">
                <span class="sui-table__strong">판매 {{ formatWon(product.soldCount) }}</span>
                <span class="sui-table__sub aprod__metric-row">
                  <button
                    type="button"
                    class="aprod__metric-link"
                    data-tour="admin-products-views"
                    :aria-label="`${product.name} 조회수 ${product.viewCount} · 전환 보기`"
                    @click="openViewsInsight(product)"
                  >
                    조회 {{ formatWon(product.viewCount) }}
                  </button>
                  <span aria-hidden="true">·</span>
                  <button
                    type="button"
                    class="aprod__metric-link"
                    data-tour="admin-products-rating"
                    :aria-label="`${product.name} 평점 ${product.rating.toFixed(1)} · 후기 보기`"
                    @click="openReviewsPreview(product)"
                  >
                    리뷰 {{ formatWon(product.reviewCount) }}
                    <template v-if="product.reviewCount"> ({{ product.rating.toFixed(1) }})</template>
                  </button>
                </span>
              </td>
              <td class="is-center">
                <div class="aprod__stock-cell" data-tour="admin-products-stock">
                  <button
                    type="button"
                    class="aprod__stock-link"
                    :class="{ 'is-low': product.stock <= 0 }"
                    :aria-label="`${product.name} 재고 ${product.stock}개 · 이력 보기`"
                    @click="openStockHistory(product)"
                  >
                    {{ formatWon(product.stock) }}개
                  </button>
                </div>
              </td>
              <td>
                <router-link
                  v-if="product.storeName"
                  class="aprod__store-link"
                  :to="{
                    path: '/admin/products',
                    query: {
                      ...(status ? { status } : {}),
                      ...(exposure ? { exposure } : {}),
                      keyword: product.storeName,
                    },
                  }"
                  @click.stop
                >
                  {{ product.storeName }}
                </router-link>
                <span v-else>—</span>
              </td>
              <td class="is-center">
                <div class="aprod__actions">
                  <router-link
                    :to="`/admin/products/${product.code}`"
                    class="sui-btn sui-btn--sm"
                    :class="product.status === 'REQUESTED' ? 'sui-btn--primary' : ''"
                    @click="onOpenProduct(product)"
                  >
                    {{ product.status === 'REQUESTED' ? '검수하기' : '열기' }}
                  </router-link>
                  <button
                    v-if="product.status === 'SELLING' || product.status === 'SOLDOUT'"
                    type="button"
                    class="sui-btn sui-btn--sm"
                    :disabled="busyCode === product.code"
                    @click="changeStatus(product, 'HIDDEN', '몰에서 내림')"
                  >
                    내리기
                  </button>
                  <button
                    v-if="product.status === 'HIDDEN'"
                    type="button"
                    class="sui-btn sui-btn--sm sui-btn--primary"
                    :disabled="busyCode === product.code"
                    @click="changeStatus(product, 'SELLING', '재판매')"
                  >
                    재판매
                  </button>
                  <button
                    v-if="product.status === 'SELLING' || product.status === 'SOLDOUT' || product.status === 'HIDDEN'"
                    type="button"
                    class="sui-btn sui-btn--sm"
                    :disabled="busyCode === product.code"
                    @click="changeStatus(product, 'REQUESTED', '재검수로 회수')"
                  >
                    재검수
                  </button>
                  <button
                    type="button"
                    class="sui-btn sui-btn--sm"
                    :disabled="busyCode === product.code"
                    @click="onDuplicate(product)"
                  >
                    복제
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="sui-empty">
        <AppIcon icon="mdi-package-variant" :size="32" />
        <p>{{ loading ? '불러오는 중입니다…' : '조건에 맞는 상품이 없습니다.' }}</p>
      </div>
    </section>

    <ManageProductStorefrontModal
      v-if="previewCode"
      :code="previewCode"
      audience="admin"
      :mall-open="previewMallOpen"
      :initial-tab="previewTab"
      @close="closePreview"
    />

    <ProductViewsInsightModal
      v-if="viewsInsight"
      :product-code="viewsInsight.code"
      :product-name="viewsInsight.name"
      :views="viewsInsight.views"
      :sold-count="viewsInsight.soldCount"
      @close="viewsInsight = null"
      @preview="openPreviewFromViews"
    />

    <ProductStockHistoryModal
      v-if="stockHistoryCode"
      :product-code="stockHistoryCode"
      :product-name="stockHistoryName"
      audience="admin"
      @close="closeStockHistory"
      @updated="onStockUpdated"
    />

    <transition name="sui-toast">
      <p v-if="toast" class="sui-toast">
        <AppIcon icon="mdi-check-circle-outline" :size="16" />
        {{ toast }}
      </p>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import ManageProductStorefrontModal from '@/components/manage/ManageProductStorefrontModal.vue'
import ProductStockHistoryModal from '@/components/manage/ProductStockHistoryModal.vue'
import ProductViewsInsightModal from '@/components/manage/ProductViewsInsightModal.vue'
import {
  duplicateAdminProduct,
  setAdminProductStatus,
} from '@/api/admin'
import { useAdminProducts } from '@/composables/useAdminProducts'
import { useListDateRange } from '@/composables/useListDateRange'
import {
  MALL_PRODUCT_STATUS_LABEL,
  isMallLiveStatus,
  mallLiveLabel,
  type MallProductStatus,
  type ServerMallProduct,
} from '@/api/mallProduct'
import { formatWon } from '@/constants/sellerData'
import { marginOf, type MarginBreakdown } from '@/constants/productEditor'
import { saveRequestedProductQueue } from '@/utils/adminProductQueue'

type ExposureKey = '' | 'deal' | 'badge' | 'plain'
type SortKey = 'newest' | 'marginDesc' | 'marginAsc' | 'priceDesc' | 'soldDesc'

const route = useRoute()
const router = useRouter()
const { products, counts, loading, errorMessage, toast, load, notify } = useAdminProducts()
const { fromDate, toDate, preset, presets, applyPreset, onManualDateChange, inRange } = useListDateRange('3m')

const status = ref(normalizeAdminStatus(String(route.query.status ?? '')))
const keyword = ref(String(route.query.keyword || route.query.seller || ''))
const exposure = ref<ExposureKey>(normalizeExposure(route.query.exposure))
const stockLow = ref(String(route.query.stock || '') === 'low')
const sortKey = ref<SortKey>('newest')
const busyCode = ref('')
const actionError = ref('')
const previewCode = ref('')
const previewMallOpen = ref(false)
const previewTab = ref<'detail' | 'review' | 'qna' | 'shipping'>('detail')
const stockHistoryCode = ref('')
const stockHistoryName = ref('')
const viewsInsight = ref<{
  code: string
  name: string
  views: number
  soldCount: number
  mallOpen: boolean
} | null>(null)
const LOW_STOCK_THRESHOLD = 30

function openPreview(
  product: ServerMallProduct,
  tab: 'detail' | 'review' | 'qna' | 'shipping' = 'detail',
) {
  previewTab.value = tab
  previewCode.value = product.code
  previewMallOpen.value = isLive(product)
}

function closePreview() {
  previewCode.value = ''
  previewMallOpen.value = false
  previewTab.value = 'detail'
}

function openViewsInsight(product: ServerMallProduct) {
  viewsInsight.value = {
    code: product.code,
    name: product.name,
    views: product.viewCount,
    soldCount: product.soldCount,
    mallOpen: isLive(product),
  }
}

function openPreviewFromViews() {
  const item = viewsInsight.value
  if (!item) return
  viewsInsight.value = null
  previewTab.value = 'detail'
  previewCode.value = item.code
  previewMallOpen.value = item.mallOpen
}

function openReviewsPreview(product: ServerMallProduct) {
  openPreview(product, 'review')
}

function openStockHistory(product: ServerMallProduct) {
  stockHistoryCode.value = product.code
  stockHistoryName.value = product.name
  syncQuery()
}

function closeStockHistory() {
  stockHistoryCode.value = ''
  stockHistoryName.value = ''
  syncQuery()
}

function onStockUpdated(payload: { code: string; stock: number; status?: string }) {
  const row = products.value.find((item) => item.code === payload.code)
  if (row) {
    row.stock = payload.stock
    if (payload.status) row.status = payload.status as MallProductStatus
  }
  notify(`재고 ${payload.stock.toLocaleString('ko-KR')}개로 반영했습니다.`)
}

const STATUS_TONE: Record<MallProductStatus, string> = {
  DRAFT: 'sui-badge--gray',
  REQUESTED: 'sui-badge--violet',
  SELLING: 'sui-badge--green',
  SOLDOUT: 'sui-badge--amber',
  HIDDEN: 'sui-badge--gray',
  REJECTED: 'sui-badge--red',
}

const statusTabs = computed(() => {
  const total = Object.values(counts.value).reduce((sum, value) => sum + (value ?? 0), 0)
  const requested = counts.value.REQUESTED ?? 0

  return [
    { key: '', label: '전체', count: total, alert: false },
    { key: 'SELLING', label: '판매중', count: counts.value.SELLING ?? 0, alert: false },
    { key: 'REQUESTED', label: '검수요청', count: requested, alert: requested > 0 },
    { key: 'SOLDOUT', label: '품절', count: counts.value.SOLDOUT ?? 0, alert: false },
    { key: 'HIDDEN', label: '숨김', count: counts.value.HIDDEN ?? 0, alert: false },
    { key: 'REJECTED', label: '반려', count: counts.value.REJECTED ?? 0, alert: false },
  ]
})

function normalizeAdminStatus(value: string) {
  return value === 'DRAFT' ? '' : value
}

function statusLabel(value: MallProductStatus) {
  if (value === 'REQUESTED') return '검수요청'
  return MALL_PRODUCT_STATUS_LABEL[value]
}

const datedProducts = computed(() =>
  products.value.filter((product) => {
    const date = product.updatedAt || product.requestedAt || product.reviewedAt || product.createdAt
    if (!date) return true
    return inRange(date)
  }),
)

const lowStockCount = computed(
  () => datedProducts.value.filter((p) => p.stock <= LOW_STOCK_THRESHOLD).length,
)

const exposureChips = computed(() => {
  const list = datedProducts.value
  return [
    { key: '' as ExposureKey, label: '노출 전체', count: list.length },
    { key: 'deal' as ExposureKey, label: '이달의 특가', count: list.filter((p) => p.isDeal).length },
    { key: 'badge' as ExposureKey, label: '배지', count: list.filter((p) => !!p.badge).length },
    {
      key: 'plain' as ExposureKey,
      label: '일반',
      count: list.filter((p) => isLive(p) && !p.isDeal && !p.badge).length,
    },
  ]
})

const visibleProducts = computed(() => {
  let list = datedProducts.value.filter((product) => matchesExposure(product, exposure.value))
  if (stockLow.value) {
    list = list.filter((product) => product.stock <= LOW_STOCK_THRESHOLD)
  }
  const sorted = [...list]
  switch (sortKey.value) {
    case 'marginDesc':
      sorted.sort((a, b) => adminMarginOf(b).platform - adminMarginOf(a).platform)
      break
    case 'marginAsc':
      sorted.sort((a, b) => adminMarginOf(a).platform - adminMarginOf(b).platform)
      break
    case 'priceDesc':
      sorted.sort((a, b) => b.price - a.price)
      break
    case 'soldDesc':
      sorted.sort((a, b) => b.soldCount - a.soldCount)
      break
    default:
      if (stockLow.value) {
        sorted.sort((a, b) => a.stock - b.stock || b.soldCount - a.soldCount)
      } else {
        sorted.sort((a, b) => {
          const da = String(a.updatedAt || a.requestedAt || a.reviewedAt || a.createdAt || '')
          const db = String(b.updatedAt || b.requestedAt || b.reviewedAt || b.createdAt || '')
          return db.localeCompare(da)
        })
      }
  }
  return sorted
})

function normalizeExposure(value: unknown): ExposureKey {
  const key = String(value || '')
  if (key === 'deal' || key === 'badge' || key === 'plain') return key
  return ''
}

function isLive(product: ServerMallProduct): boolean {
  return isMallLiveStatus(product.status)
}

function adminMarginOf(product: ServerMallProduct): MarginBreakdown & { share: number } {
  const base = marginOf({
    price: product.price,
    cost: product.cost,
    marginShare: product.marginShare,
  })
  return {
    ...base,
    share: Math.min(100, Math.max(0, Number(product.marginShare) || 0)),
  }
}

/** 판매가 대비 관리자(함우리) 마진율 */
function adminMarginRate(product: ServerMallProduct): number {
  const price = Math.max(0, product.price || 0)
  if (price <= 0) return 0
  return Math.round((adminMarginOf(product).platform / price) * 1000) / 10
}

function matchesExposure(product: ServerMallProduct, key: ExposureKey): boolean {
  if (!key) return true
  if (key === 'deal') return !!product.isDeal
  if (key === 'badge') return !!product.badge
  if (key === 'plain') return isLive(product) && !product.isDeal && !product.badge
  return true
}

function statusTone(value: MallProductStatus): string {
  return STATUS_TONE[value] ?? 'sui-badge--gray'
}

function badgeTone(badge: string): string {
  if (badge === '특가') return 'aprod__tag--urgent'
  if (badge === '인기' || badge === '베스트') return 'aprod__tag--hot'
  if (badge === 'NEW') return 'aprod__tag--new'
  return 'aprod__tag--badge'
}

function reload() {
  actionError.value = ''
  load({ status: status.value, keyword: keyword.value })
}

function syncQuery() {
  const query: Record<string, string> = {}
  if (status.value) query.status = status.value
  if (keyword.value) query.keyword = keyword.value
  if (exposure.value) query.exposure = exposure.value
  if (stockLow.value) query.stock = 'low'
  if (stockHistoryCode.value) query.code = stockHistoryCode.value
  router.replace({ path: '/admin/products', query })
}

function toggleStockLow() {
  stockLow.value = !stockLow.value
  syncQuery()
}

function setStatus(next: string) {
  status.value = next
  syncQuery()
  reload()
}

function setExposure(next: ExposureKey) {
  exposure.value = next
  syncQuery()
}

function reloadAndSync() {
  syncQuery()
  reload()
}

onMounted(async () => {
  await reload()
  const code = String(route.query.code || '').trim()
  if (code) {
    stockHistoryCode.value = code
    stockHistoryName.value = products.value.find((p) => p.code === code)?.name || ''
  }
})

watch(
  () => [
    route.query.status,
    route.query.keyword,
    route.query.seller,
    route.query.exposure,
    route.query.stock,
    route.query.code,
  ],
  () => {
    const nextStatus = normalizeAdminStatus(String(route.query.status ?? ''))
    const nextKeyword = String(route.query.keyword || route.query.seller || '')
    const nextExposure = normalizeExposure(route.query.exposure)
    const nextStockLow = String(route.query.stock || '') === 'low'
    const nextCode = String(route.query.code || '').trim()
    let needReload = false
    if (nextStatus !== status.value) {
      status.value = nextStatus
      needReload = true
    }
    if (nextKeyword !== keyword.value) {
      keyword.value = nextKeyword
      needReload = true
    }
    if (nextExposure !== exposure.value) {
      exposure.value = nextExposure
    }
    stockLow.value = nextStockLow
    if (nextCode) {
      stockHistoryCode.value = nextCode
      stockHistoryName.value =
        products.value.find((p) => p.code === nextCode)?.name || stockHistoryName.value
    } else {
      stockHistoryCode.value = ''
      stockHistoryName.value = ''
    }
    if (needReload) reload()
  },
)

function onOpenProduct(product: ServerMallProduct) {
  if (product.status !== 'REQUESTED') return
  const codes = visibleProducts.value
    .filter((row) => row.status === 'REQUESTED')
    .map((row) => row.code)
  if (codes.length) saveRequestedProductQueue(codes)
}

async function onDuplicate(product: ServerMallProduct) {
  if (busyCode.value) return
  if (!window.confirm(`'${product.name}'을(를) 복제할까요? 복제본은 임시저장으로 만들어집니다.`)) {
    return
  }
  actionError.value = ''
  busyCode.value = product.code
  try {
    const { product: cloned, message } = await duplicateAdminProduct(product.code)
    notify(message || '상품을 복제했습니다.')
    await load({ status: status.value, keyword: keyword.value })
    router.push(`/admin/products/${cloned.code}`)
  } catch (error) {
    actionError.value = error instanceof Error ? error.message : '상품 복제에 실패했습니다.'
  } finally {
    busyCode.value = ''
  }
}

async function changeStatus(product: ServerMallProduct, next: MallProductStatus, memo: string) {
  actionError.value = ''
  busyCode.value = product.code
  try {
    const result = await setAdminProductStatus(product.code, next, memo)
    notify(result.message || '판매 상태를 바꿨습니다.')
    const wasOnMall = status.value === 'SELLING' || status.value === 'SOLDOUT' || status.value === 'HIDDEN'
    if (next === 'REQUESTED' && wasOnMall) {
      setStatus('REQUESTED')
      return
    }
    await load({ status: status.value, keyword: keyword.value })
  } catch (error) {
    actionError.value = error instanceof Error ? error.message : '판매 상태 변경에 실패했습니다.'
  } finally {
    busyCode.value = ''
  }
}

function formatDate(value: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
    date.getDate(),
  ).padStart(2, '0')}`
}
</script>

<style lang="scss" src="./AdminProductsPage.scss"></style>
